import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getPendingUsers, getApprovedUsers, getAdminStats, approveUser, rejectUser, deleteUser } from '../services/adminService';
import { 
  ArrowLeftIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ClockIcon,
  UsersIcon,
  TrashIcon 
} from '@heroicons/react/24/outline';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' or 'approved'

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pendingResponse, approvedResponse, statsResponse] = await Promise.all([
        getPendingUsers(),
        getApprovedUsers(),
        getAdminStats()
      ]);
      setPendingUsers(pendingResponse.users);
      setApprovedUsers(approvedResponse.users);
      setStats(statsResponse.stats);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      alert('Veri yüklenirken hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId, userName) => {
    if (!window.confirm(`${userName} kullanıcısını onaylamak istediğinizden emin misiniz?`)) {
      return;
    }

    setActionLoading(userId);
    try {
      await approveUser(userId);
      alert('✅ Kullanıcı başarıyla onaylandı!');
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Approve error:', error);
      alert('❌ Hata: ' + (error.message || 'Kullanıcı onaylanamadı'));
    } finally {
      setActionLoading(null);
    }
  };

  const openRejectModal = (userId, userName) => {
    setSelectedUser({ id: userId, name: userName });
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert('Lütfen red sebebini belirtin');
      return;
    }

    setActionLoading(selectedUser.id);
    try {
      await rejectUser(selectedUser.id, rejectReason);
      alert('✅ Kullanıcı reddedildi');
      setShowRejectModal(false);
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Reject error:', error);
      alert('❌ Hata: ' + (error.message || 'Kullanıcı reddedilemedi'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`⚠️ ${userName} kullanıcısını kalıcı olarak silmek istediğinizden emin misiniz?\n\nBu işlem geri alınamaz ve kullanıcının tüm verileri silinecektir!`)) {
      return;
    }

    setActionLoading(userId);
    try {
      await deleteUser(userId);
      alert('✅ Kullanıcı başarıyla silindi');
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Delete error:', error);
      alert('❌ Hata: ' + (error.message || 'Kullanıcı silinemedi'));
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Admin kontrolü
  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">🚫 Yetkisiz Erişim</h1>
          <p className="text-gray-600 mb-8">Bu sayfaya erişim yetkiniz yok.</p>
          <Link
            to="/dashboard"
            className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Dashboard'a Dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/dashboard"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Dashboard'a Dön
          </Link>

          <h1 className="text-3xl font-bold text-gray-900">🔐 Admin Paneli</h1>
          <p className="text-gray-600 mt-1">Kullanıcı yönetimi ve onay işlemleri</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Toplam Kullanıcı</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <UsersIcon className="h-12 w-12 text-blue-500" />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-yellow-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Bekleyen</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.pending}</p>
                </div>
                <ClockIcon className="h-12 w-12 text-yellow-500" />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Onaylı</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.approved}</p>
                </div>
                <CheckCircleIcon className="h-12 w-12 text-green-500" />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-red-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Reddedilen</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.rejected}</p>
                </div>
                <XCircleIcon className="h-12 w-12 text-red-500" />
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-md mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('pending')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'pending'
                  ? 'border-b-2 border-primary-600 text-primary-600 bg-primary-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center">
                <ClockIcon className="w-5 h-5 mr-2" />
                Onay Bekleyenler
                <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                  activeTab === 'pending' 
                    ? 'bg-primary-200 text-primary-800' 
                    : 'bg-gray-200 text-gray-700'
                }`}>
                  {pendingUsers.length}
                </span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('approved')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'approved'
                  ? 'border-b-2 border-primary-600 text-primary-600 bg-primary-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center">
                <CheckCircleIcon className="w-5 h-5 mr-2" />
                Onaylı Kullanıcılar
                <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                  activeTab === 'approved' 
                    ? 'bg-primary-200 text-primary-800' 
                    : 'bg-gray-200 text-gray-700'
                }`}>
                  {approvedUsers.length}
                </span>
              </div>
            </button>
          </div>
        </div>

        {/* Pending Users Tab */}
        {activeTab === 'pending' && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">⏳ Bekleyen Kullanıcılar</h2>
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-semibold">
                {pendingUsers.length} Kullanıcı
              </span>
            </div>

          {pendingUsers.length === 0 ? (
            <div className="text-center py-12">
              <ClockIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Bekleyen kullanıcı yok</p>
              <p className="text-gray-400 text-sm mt-2">Tüm kayıtlar onaylanmış durumda</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kullanıcı
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Alan
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mezuniyet
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kayıt Tarihi
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      İşlemler
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pendingUsers.map((pendingUser) => (
                    <tr key={pendingUser.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{pendingUser.name}</div>
                          <div className="text-sm text-gray-500">{pendingUser.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          pendingUser.targetField === 'SAY' ? 'bg-blue-100 text-blue-800' :
                          pendingUser.targetField === 'EA' ? 'bg-green-100 text-green-800' :
                          pendingUser.targetField === 'SOZ' ? 'bg-purple-100 text-purple-800' :
                          'bg-pink-100 text-pink-800'
                        }`}>
                          {pendingUser.targetField}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {pendingUser.graduationYear}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(pendingUser.createdAt).toLocaleDateString('tr-TR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleApprove(pendingUser.id, pendingUser.name)}
                          disabled={actionLoading === pendingUser.id}
                          className="inline-flex items-center px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition disabled:opacity-50"
                        >
                          <CheckCircleIcon className="w-4 h-4 mr-1" />
                          Onayla
                        </button>
                        <button
                          onClick={() => openRejectModal(pendingUser.id, pendingUser.name)}
                          disabled={actionLoading === pendingUser.id}
                          className="inline-flex items-center px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition disabled:opacity-50"
                        >
                          <XCircleIcon className="w-4 h-4 mr-1" />
                          Reddet
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          </div>
        )}

        {/* Approved Users Tab */}
        {activeTab === 'approved' && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">✅ Onaylı Kullanıcılar</h2>
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                {approvedUsers.length} Kullanıcı
              </span>
            </div>

            {approvedUsers.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircleIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">Henüz onaylı kullanıcı yok</p>
                <p className="text-gray-400 text-sm mt-2">Bekleyen kullanıcıları onaylayarak başlayabilirsiniz</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Kullanıcı
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Alan
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Mezuniyet
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Onay Tarihi
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email Durumu
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        İşlemler
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {approvedUsers.map((approvedUser) => (
                      <tr key={approvedUser.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{approvedUser.name}</div>
                            <div className="text-sm text-gray-500">{approvedUser.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            approvedUser.targetField === 'SAY' ? 'bg-blue-100 text-blue-800' :
                            approvedUser.targetField === 'EA' ? 'bg-green-100 text-green-800' :
                            approvedUser.targetField === 'SOZ' ? 'bg-purple-100 text-purple-800' :
                            'bg-pink-100 text-pink-800'
                          }`}>
                            {approvedUser.targetField}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {approvedUser.graduationYear}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {approvedUser.approvedAt ? new Date(approvedUser.approvedAt).toLocaleDateString('tr-TR') : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {approvedUser.isEmailVerified ? (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              ✓ Doğrulandı
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                              Bekliyor
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleDelete(approvedUser.id, approvedUser.name)}
                            disabled={actionLoading === approvedUser.id}
                            className="inline-flex items-center px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition disabled:opacity-50"
                          >
                            <TrashIcon className="w-4 h-4 mr-1" />
                            Sil
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Reject Modal */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Kullanıcıyı Reddet
              </h3>
              <p className="text-gray-600 mb-4">
                <strong>{selectedUser?.name}</strong> kullanıcısını reddetmek istediğinizden emin misiniz?
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Red Sebebi *
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  rows="3"
                  placeholder="Kullanıcıya gönderilecek red sebebi..."
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowRejectModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                >
                  İptal
                </button>
                <button
                  onClick={handleReject}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                >
                  {actionLoading ? 'Reddediliyor...' : 'Reddet'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
